import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import BrandsModelsSelect from '@/components/_common/BrandsModelsSelect';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { CrmCustomerGender } from '@/core/domain/crm/CrmList/CrmList.types';
import { SendOptionType } from '@/core/domain/crm/SendOptions/SendOptions.types';
import { useWindowSize } from '@/hooks';
import { useQueryParams } from '@/hooks/useQueryParams';
import { Button, Col, Form, Input, Radio, Row, Typography } from 'antd';
import { Moment } from 'moment';

export interface CrmFilterFormValues {
  name: string;
  monthStart?: Moment;
  monthEnd?: Moment;
  initialDate?: Moment;
  finalDate?: Moment;
  dueDateInitial?: Moment;
  dueDateFinal?: Moment;
  companyId?: number;
  toInvoiceDate?: Moment;
  genereType?: 'F' | 'M' | null;
}

interface Props {
  onFilter(filter: CrmFilterFormValues): void;
}

const CrmCustomerListFilter: React.FC<Props> = ({ onFilter }) => {
  const [form] = Form.useForm<CrmFilterFormValues>();
  const formValues = Form.useWatch([], form);
  const { isMobile } = useWindowSize();

  const initialDate = Form.useWatch('initialDate', form);
  const finalDate = Form.useWatch('finalDate', form);

  const dueDateInitial = Form.useWatch('dueDateInitial', form);
  const dueDateFinal = Form.useWatch('dueDateFinal', form);

  const { type } = useQueryParams<{ type?: SendOptionType }>();

  const showDates =
    type === SendOptionType.birthdays ||
    type === SendOptionType.futureServices ||
    type === SendOptionType.satisfactionSurvey ||
    type === SendOptionType.clientWithoutMovement ||
    type === SendOptionType.nextReviews;

  const showCompany = type === SendOptionType.recoveryOfDebts;

  const showDueDates = type === SendOptionType.recoveryOfDebts;

  const showModelsBrands =
    type === SendOptionType.custom ||
    type === SendOptionType.emailMarketing ||
    type === SendOptionType.futureServices ||
    type === SendOptionType.nextReviews;

  const showGenere =
    type === SendOptionType.custom || type === SendOptionType.emailMarketing;

  const hasValues =
    !!formValues && Object.values(formValues).some((value) => !!value);

  function handleReset() {
    form.resetFields();
    onFilter(form.getFieldsValue());
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFilter}>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col flex={1}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Filtro de clientes
          </Typography.Title>
        </Col>

        <Col span={isMobile ? 24 : undefined}>
          <SpaceForButton>
            <Button onClick={handleReset} disabled={!hasValues} block>
              Resetar
            </Button>

            <Button htmlType="submit" type="primary" block>
              Filtrar
            </Button>
          </SpaceForButton>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            style={{ margin: 0 }}
            label="Pesquisar cliente"
            name="name"
          >
            <Input />
          </FloatingFormItem>
        </Col>

        {showDates && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              style={{ margin: 0 }}
              label="Data inicial"
              name="initialDate"
            >
              <InputDate disabledDate={(date) => date.isAfter(finalDate)} />
            </FloatingFormItem>
          </Col>
        )}

        {showDates && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              style={{ margin: 0 }}
              label="Data final"
              name="finalDate"
            >
              <InputDate disabledDate={(date) => date.isBefore(initialDate)} />
            </FloatingFormItem>
          </Col>
        )}

        {showCompany && (
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              style={{ margin: 0 }}
              label="Selecionar uma empresa"
              name="companyId"
            >
              <CompaniesInfiniteSelectField allowClear />
            </FloatingFormItem>
          </Col>
        )}

        {showDueDates && (
          <>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                style={{ margin: 0 }}
                label="Data inicial do vencimento"
                name="dueDateInitial"
              >
                <InputDate
                  disabledDate={(date) => date.isAfter(dueDateFinal)}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                style={{ margin: 0 }}
                label="Data final do vencimento"
                name="dueDateFinal"
              >
                <InputDate
                  disabledDate={(date) => date.isBefore(dueDateInitial)}
                />
              </FloatingFormItem>
            </Col>
          </>
        )}

        {showModelsBrands && (
          <Col span={24} md={12} lg={6}>
            <BrandsModelsSelect
              form={
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                form as any
              }
            />
          </Col>
        )}

        {showGenere && (
          <Col span={24} md={12} lg={6}>
            <span style={{ fontWeight: 600 }}>Gênero</span>
            <Form.Item name="gender">
              <Radio.Group defaultValue={null}>
                <Radio value={null}>Ambos</Radio>
                <Radio value={CrmCustomerGender.Woman}>Feminino</Radio>
                <Radio value={CrmCustomerGender.Man}>Masculino</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default CrmCustomerListFilter;
